/**
 * Fallübersicht-Spalten für den Forderungsmanager (Issue #263).
 *
 * Stellt das Spalten-Katalog, die Default-Sortierung (Verjährung asc, dann
 * offener Betrag desc) und die Persistenz pro User bereit.
 *
 * Spalten, die erst von Schwester-Issues (#256/#257/#258) befüllt werden,
 * sind enthalten und werden im UI mit einem Platzhalter ('—') gerendert,
 * bis die Daten geliefert werden.
 */

import type { Forderungsakte } from '../hooks/useForderungen';

export type ColumnId =
  | 'aktenNr'
  | 'status'
  | 'gegner'
  | 'versicherungGegner'
  | 'schadenNummer'
  | 'abtretung'
  | 'anwalt'
  | 'unfallDatum'
  | 'verjaehrung'
  | 'offenerBetrag'
  | 'plausibilitaet'
  | 'crossLink'
  | 'letzteAktion';

export interface ColumnDef {
  id: ColumnId;
  label: string;
  alwaysVisible?: boolean;
}

export const COLUMN_DEFS: readonly ColumnDef[] = [
  { id: 'aktenNr', label: 'Akten-Nr.', alwaysVisible: true },
  { id: 'status', label: 'Status', alwaysVisible: true },
  { id: 'gegner', label: 'Unfallgegner' },
  { id: 'versicherungGegner', label: 'Versicherung Gegner' },
  { id: 'schadenNummer', label: 'Schadennummer' },
  { id: 'abtretung', label: 'Abtretung' },
  { id: 'anwalt', label: 'Anwalt' },
  { id: 'unfallDatum', label: 'Unfalldatum' },
  { id: 'verjaehrung', label: 'Verjährung' },
  { id: 'offenerBetrag', label: 'Offener Betrag + Mahnstufe' },
  { id: 'plausibilitaet', label: 'Plausibilitäts-Ampel' },
  { id: 'crossLink', label: 'Verknüpfungen' },
  { id: 'letzteAktion', label: 'Letzte Aktion' },
];

export const ALWAYS_VISIBLE_COLUMNS: readonly ColumnId[] = COLUMN_DEFS
  .filter(c => c.alwaysVisible)
  .map(c => c.id);

export const DEFAULT_VISIBLE_COLUMNS: readonly ColumnId[] = [
  'aktenNr',
  'status',
  'gegner',
  'versicherungGegner',
  'schadenNummer',
  'verjaehrung',
  'offenerBetrag',
];

export type ColumnVisibility = Partial<Record<ColumnId, boolean>>;

export function normalizeVisibility(vis: ColumnVisibility): Record<ColumnId, boolean> {
  const out = {} as Record<ColumnId, boolean>;
  for (const col of COLUMN_DEFS) {
    out[col.id] = DEFAULT_VISIBLE_COLUMNS.includes(col.id);
  }
  for (const key of Object.keys(vis) as ColumnId[]) {
    const v = vis[key];
    if (typeof v === 'boolean') out[key] = v;
  }
  for (const id of ALWAYS_VISIBLE_COLUMNS) out[id] = true;
  return out;
}

// ─── Persistenz ──────────────────────────────────────────────

const STORAGE_KEY_PREFIX = 'forderungen:column-visibility:';

export function storageKeyForUser(userId: string | null | undefined): string {
  return `${STORAGE_KEY_PREFIX}${userId || 'anonymous'}`;
}

export function loadColumnVisibility(userId: string | null | undefined): Record<ColumnId, boolean> {
  try {
    if (typeof localStorage === 'undefined') return normalizeVisibility({});
    const raw = localStorage.getItem(storageKeyForUser(userId));
    if (!raw) return normalizeVisibility({});
    const parsed = JSON.parse(raw) as ColumnVisibility;
    return normalizeVisibility(parsed ?? {});
  } catch {
    return normalizeVisibility({});
  }
}

export function saveColumnVisibility(
  userId: string | null | undefined,
  vis: ColumnVisibility,
): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(storageKeyForUser(userId), JSON.stringify(normalizeVisibility(vis)));
  } catch {
    /* localStorage quota / disabled — UI bleibt funktional, nur nicht persistent */
  }
}

// ─── Sortierung ──────────────────────────────────────────────

function verjaehrungTime(f: Forderungsakte): number {
  if (!f.verjährungsfrist) return Number.POSITIVE_INFINITY;
  const t = new Date(f.verjährungsfrist).getTime();
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

/**
 * Default-Sort für die Fallübersicht:
 *  1. Verjährung aufsteigend (drängendste zuerst; keine Frist ganz ans Ende).
 *  2. Offener Betrag (Brutto) absteigend.
 */
export function sortForderungenDefault(
  forderungen: readonly Forderungsakte[],
): Forderungsakte[] {
  return [...forderungen].sort((a, b) => {
    const va = verjaehrungTime(a);
    const vb = verjaehrungTime(b);
    if (va !== vb) return va - vb;
    return (b.gesamtbetrag ?? 0) - (a.gesamtbetrag ?? 0);
  });
}
